import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, MapPin, Clock, ArrowUpRight, Send } from "lucide-react";
import { images, site, whatsappUrl } from "../data/siteData";
import { PageHero } from "../components/PageHero";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/Reveal";

const initialForm = { name: "", phone: "", email: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.title = "Contact | Mango – World Cuisine Restaurant, Bhavnagar";
  }, []);

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      // Simulate client-side submission with instant response
      await new Promise((resolve) => setTimeout(resolve, 600));
      
      // Save locally so messages are preserved in browser
      const stored = JSON.parse(localStorage.getItem("mango_inquiries") || "[]");
      stored.push({ ...form, id: Date.now(), timestamp: new Date().toISOString() });
      localStorage.setItem("mango_inquiries", JSON.stringify(stored));

      toast.success("Message sent! We will get back to you shortly.");
      setForm(initialForm);
    } catch {
      toast.error("Could not send your message. Please call or WhatsApp us instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-line bg-white px-4 py-3.5 text-sm text-charcoal placeholder:text-charcoal/40 focus:border-gold focus:outline-none transition-colors duration-300";

  return (
    <>
      <PageHero
        overline="Contact"
        lines={["Say hello", "to Mango."]}
        image={images.heroContact}
      />

      <section data-testid="contact-section" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
          <div>
            <SectionHeading overline="Find Us" title={site.fullName} />
            <address className="mt-8 not-italic flex flex-col gap-6">
              <p className="flex gap-4 text-charcoal/80 leading-relaxed">
                <MapPin size={20} className="shrink-0 mt-1 text-forest" />
                <span>
                  {site.addressLines.map((l) => (
                    <span key={l} className="block">{l}</span>
                  ))}
                </span>
              </p>
              <p className="flex gap-4 items-center text-charcoal/80">
                <Phone size={20} className="shrink-0 text-forest" />
                <a href={`tel:${site.phoneTel}`} data-testid="contact-phone-link" className="hover:text-forest transition-colors duration-300">
                  {site.phoneDisplay}
                </a>
              </p>
              <p className="flex gap-4 items-center text-charcoal/80">
                <Clock size={20} className="shrink-0 text-forest" />
                <span data-testid="contact-hours">{site.hours}</span>
              </p>
            </address>
            <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
              <a
                href={`tel:${site.phoneTel}`}
                data-testid="contact-call-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-forest px-7 py-3.5 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep hover:-translate-y-0.5"
              >
                <Phone size={15} /> Call Now
              </a>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:brightness-95 hover:-translate-y-0.5"
              >
                WhatsApp
              </a>
              <a
                href={site.directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-directions-button"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-forest/25 px-7 py-3.5 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-forest hover:text-ivory"
              >
                Get Directions <ArrowUpRight size={15} />
              </a>
            </div>
          </div>

          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-[2rem] bg-sand p-8 md:p-10 border border-line"
            >
              <h2 className="font-display text-2xl md:text-3xl text-charcoal">Send us a message</h2>
              <p className="mt-2 text-sm text-charcoal/60">
                Bookings, celebrations or just a question — we reply fast.
              </p>
              <div className="mt-8 flex flex-col gap-4">
                <input
                  name="name"
                  value={form.name}
                  onChange={update}
                  required
                  placeholder="Name"
                  aria-label="Your name"
                  data-testid="contact-form-name-input"
                  className={inputCls}
                />
                <input
                  name="phone"
                  value={form.phone}
                  onChange={update}
                  required
                  type="tel"
                  placeholder="Phone Number"
                  aria-label="Your phone number"
                  data-testid="contact-form-phone-input"
                  className={inputCls}
                />
                <input
                  name="email"
                  value={form.email}
                  onChange={update}
                  type="email"
                  placeholder="Email"
                  aria-label="Your email"
                  data-testid="contact-form-email-input"
                  className={inputCls}
                />
                <textarea
                  name="message"
                  value={form.message}
                  onChange={update}
                  required
                  rows={4}
                  placeholder="Message"
                  aria-label="Your message"
                  data-testid="contact-form-message-input"
                  className={`${inputCls} resize-none`}
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-form-submit-button"
                  className="group mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {sending ? "Sending…" : "Send Message"}
                  <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </form>
          </Reveal>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-16">
          <Reveal>
            <div className="overflow-hidden rounded-[2rem] border border-line shadow-sm">
              <iframe
                title="Mango – World Cuisine Restaurant on Google Maps"
                data-testid="contact-map-embed"
                src={site.mapEmbedUrl}
                className="h-[380px] md:h-[440px] w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
